import { ClickAwayListener, MenuItem, MenuList, Paper, Popper, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { PopperModifiers } from "src/common/components/Autocomplete/Popper.config";

const useStyles = makeStyles((theme: any) => ({
  buttonWrapper: {
    "&:before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="5" viewBox="0 0 10 5"><path fill="%234a90e2" fill-rule="nonzero" d="M0 0l5 5 5-5z"/></svg>')`,
      backgroundSize: "100% 100%",
      content: '""',
      display: "inline-block",
      height: 9,
      marginTop: "-4.5px",
      position: "absolute" as "absolute",
      right: 8,
      top: "50%",
      width: 12
    },
    [theme.breakpoints.up("md")]: {
      minHeight: 36
    },
    background: "#ffffff",
    border: "1px solid #bdbdbd",
    borderRadius: 4,
    padding: "6px 24px 6px 12px",
    position: "relative" as "relative",
    textAlign: "left" as "left",
    width: "100%"
  },
  currentValue: {
    fontSize: "1.4rem"
  },
  label: {
    fontSize: "1rem",
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  menuItem: {
    fontSize: "1.4rem",
    height: "auto",
    whiteSpace: "initial" as "initial"
  },
  modal: {
    maxHeight: 350,
    overflow: "scroll"
  },
  open: {
    position: "relative" as "relative",
    zIndex: 2
  }
}));

interface IOptions {
  value: any;
  label: string;
}

interface IOwnProps {
  name: string;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  onChange?: (newValue: any) => void;
  value?: string;
  defaultValue?: string;
  options?: IOptions[];
  required?: boolean;
  readOnly?: boolean;
  label?: string;
  dataTestId?: string;
  setFieldValue: (name: string, value: any) => void;
  className?: string;
}

const SelectField = ({
  options = [],
  placeholder,
  label,
  setFieldValue,
  onChange,
  value,
  name,
  disabled,
  dataTestId,
  className
}: IOwnProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const anchorEl = React.useRef(null);
  const handleButtonClick = React.useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const handleOptionClick = React.useCallback(
    (newValue: any) => {
      return () => {
        setOpen(false);
        setFieldValue(name, newValue);
        if (onChange) {
          onChange(newValue);
        }
      };
    },
    [onChange, setOpen, name, setFieldValue]
  );

  const currentOption = options.find(({ value: optionValue }) => optionValue === value);

  const handleClickAway = React.useCallback(() => {
    if (open) {
      setOpen(false);
    }
  }, [setOpen, open]);

  const containerClassName = React.useMemo(() => {
    const classNames = [];
    if (open) {
      classNames.push(classes.open);
    }
    if (className) {
      classNames.push(className);
    }

    return classNames.join(" ");
  }, [open, className]);

  return (
    <div className={containerClassName}>
      <button
        className={classes.buttonWrapper}
        ref={anchorEl}
        type="button"
        onClick={handleButtonClick}
        disabled={disabled}
        data-qa-selector={dataTestId}
      >
        <div className={classes.label}>
          <span>{label}</span>
        </div>
        <div className={classes.currentValue}>
          <span>{currentOption ? currentOption.label : label}</span>
        </div>
      </button>
      <Popper
        disablePortal
        modifiers={PopperModifiers}
        anchorEl={anchorEl.current ? anchorEl.current : undefined}
        open={open}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper className={classes.modal}>
            <MenuList>
              {placeholder ? (
                <MenuItem
                  className={classes.menuItem}
                  selected={value === undefined}
                  onClick={handleOptionClick(undefined)}
                  key={value}
                  data-qa-selector={`${dataTestId}-${value}`}
                >
                  {placeholder}
                </MenuItem>
              ) : null}
              {options.map(({ value: optionValue, label: optionLabel }) => {
                return (
                  <MenuItem
                    className={classes.menuItem}
                    selected={value === optionValue}
                    onClick={handleOptionClick(optionValue)}
                    key={optionValue}
                    data-qa-selector={`${dataTestId}-${optionLabel}`}
                  >
                    {t(optionLabel)}
                  </MenuItem>
                );
              })}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

export default React.memo(SelectField);
